/* istanbul ignore file */

/*
This is a compatability layer for Safari 14+.
Due to the way react-modal is rendered in safari the initial css classes are not used and the css animations are redundant.
This component adds the animation css classes using a timeout to force both states to be rendered.
*/

import type { ReactNode } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import ReactModal from 'react-modal';

type ReactModalProps = React.ComponentProps<typeof ReactModal>;

export interface SafariCompatModalProps extends ReactModalProps {
  children: ReactNode | undefined;
}

export const SafariCompatModal = ({
  className,
  onAfterClose,
  onAfterOpen,
  overlayClassName,
  ...props
}: SafariCompatModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const setIsOpenWithTimeout = (state: boolean) => {
    setTimeout(() => {
      setIsOpen(state);
    }, 0);
  };

  const onAfterCloseHandler = useCallback(() => {
    if (onAfterClose) {
      onAfterClose();
    }
    setIsOpenWithTimeout(false);
  }, [onAfterClose]);

  const onAfterOpenHandler = useCallback(() => {
    if (onAfterOpen) {
      onAfterOpen();
    }
    setIsOpenWithTimeout(true);
  }, [onAfterOpen]);

  const overlayClasses = useMemo(() => {
    return typeof overlayClassName !== 'string' && overlayClassName?.afterOpen
      ? {
          base: overlayClassName.base,
          afterOpen: isOpen ? overlayClassName.afterOpen : '',
          beforeClose: overlayClassName.beforeClose,
        }
      : overlayClassName;
  }, [isOpen, overlayClassName]);

  const contentClasses = useMemo(() => {
    return typeof className !== 'string' && className?.afterOpen
      ? {
          base: className.base,
          afterOpen: isOpen ? className.afterOpen : '',
          beforeClose: className.beforeClose,
        }
      : className;
  }, [className, isOpen]);

  return (
    <ReactModal
      {...props}
      className={contentClasses}
      onAfterClose={onAfterCloseHandler}
      onAfterOpen={onAfterOpenHandler}
      overlayClassName={overlayClasses}
    >
      {props.children}
    </ReactModal>
  );
};
